import React from 'react';
import { Box, Stack } from '@mui/material';
import { object, string } from 'yup';
import { useFormik } from 'formik';
import { type FormikProp, type ResearchExperienceFormValues } from './types';
import { ResearchExperienceTextFiled } from './ResearchExperienceTextFiled';
import { PAGE_TEXT } from '../lang';
import { Button } from '../../../../../components';
import { handleReadySubmit } from '../../../../../utils';

export type ResearchExperienceFormProps = {
  initialValues: ResearchExperienceFormValues;
  enableEdit: boolean;
  isHKPFSApplicant: boolean;
  onSubmit: (values: ResearchExperienceFormValues) => void;
};

const pageText = PAGE_TEXT.OTHER_INFORMATION.RESEARCH_EXPERIENCE;
export const ResearchExperienceForm: React.FunctionComponent<
  ResearchExperienceFormProps
> = props => {
  const { initialValues, enableEdit, isHKPFSApplicant, onSubmit } = props;
  const formik: FormikProp = useFormik({
    initialValues,
    validationSchema: object({
      pastResearchExperience: string().required('Required'),
      proposedResearchTopic: string().required('Required'),
      proposedResearchPlan: string().required('Required'),
      reasons: string().required('Required'),
      longTermCareerPlan: string().required('Required'),
      expectedContribution: string().required('Required'),
      others: string().required('Required'),
    }),
    onSubmit,
  });
  const inputFields: Array<{
    name: string;
    label: string;
    maxLength: number;
    placeholder?: string;
  }> = [
    {
      name: 'pastResearchExperience',
      label: pageText.PAST_RESEARCH_EXPERIENCE,
      maxLength: 7500,
    },
    {
      name: 'proposedResearchTopic',
      label: pageText.PROPOSED_RESEARCH_TOPIC,
      maxLength: 500,
    },
    {
      name: 'proposedResearchPlan',
      label: pageText.PROPOSED_RESEARCH_PLAN,
      maxLength: 7500,
    },
    {
      name: 'reasons',
      label: pageText.REASONS,
      maxLength: 2000,
    },
    {
      name: 'longTermCareerPlan',
      label: pageText.LONG_TERM_CAREER_PLAN,
      maxLength: 2000,
    },
    {
      name: 'expectedContribution',
      label: pageText.EXPECTED_CONTRIBUTION,
      maxLength: 2000,
    },
    {
      name: 'others',
      label: isHKPFSApplicant
        ? pageText.HKPFS_OTHERS
        : pageText.SCHOLARSHIP_OTHERS,
      placeholder: isHKPFSApplicant
        ? undefined
        : pageText.SCHOLARSHIP_OTHERS_NOTE,
      maxLength: 2000,
    },
  ];

  return (
    <form onSubmit={handleReadySubmit(formik)} noValidate>
      <Stack>
        {/* Main Body */}
        <Box>
          {/* Keep the coding */}
          {inputFields.map(inputField => {
            const name = inputField.name as keyof ResearchExperienceFormValues;
            const { label, maxLength, placeholder } = inputField;
            return (
              <ResearchExperienceTextFiled
                enableEdit={enableEdit}
                key={name}
                name={name}
                label={label}
                placeholder={placeholder}
                maxLength={maxLength}
                value={formik.values[name]}
                error={formik.touched[name] && Boolean(formik.errors[name])}
                helperText={formik.touched[name] && formik.errors[name]}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
            );
          })}
        </Box>

        {enableEdit && (
          <Box textAlign={'center'} marginBottom={5}>
            <Button variant="contained" type="submit">
              {PAGE_TEXT.SAVE}
            </Button>
          </Box>
        )}
      </Stack>
    </form>
  );
};
